/**
 * Product faceted-filter + column model and URL (de)serialization. Shared so the
 * database Products tab and (later) the cross-database Products Overview stay in
 * sync. All filter/view/column state lives in the URL query string (spec §7.1a).
 */

export interface ProductFilters {
  status: string[];
  jurisdiction: string[];
  tier: string[];
  origin: string[];
  destination: string[];
  htsChapter: string[];
  // Boolean toggle (spec §4.6): controlled-regime products missing jurisdiction
  // detail. Kept off the string-array FILTER_FACET_KEYS and handled separately.
  needsJurisdiction: boolean;
  // Cross-database Products Overview only (spec §5.2): owning database ids.
  // Kept off FILTER_FACET_KEYS (its chip needs id→name mapping, handled in the bar).
  database: string[];
}

export const FILTER_FACET_KEYS = [
  'status',
  'jurisdiction',
  'tier',
  'origin',
  'destination',
  'htsChapter',
] as const;
export type FilterFacetKey = (typeof FILTER_FACET_KEYS)[number];

const NEEDS_JUR_PARAM = 'needsjur';
const DATABASE_PARAM = 'db';

export const EMPTY_FILTERS: ProductFilters = {
  status: [],
  jurisdiction: [],
  tier: [],
  origin: [],
  destination: [],
  htsChapter: [],
  needsJurisdiction: false,
  database: [],
};

// Facet key → URL param name.
const URL_KEY: Record<FilterFacetKey, string> = {
  status: 'status',
  jurisdiction: 'jurisdiction',
  tier: 'tier',
  origin: 'origin',
  destination: 'dest',
  htsChapter: 'hts',
};

const splitParam = (raw: string | null): string[] =>
  (raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export const parseFilters = (sp: URLSearchParams): ProductFilters => ({
  status: splitParam(sp.get(URL_KEY.status)),
  jurisdiction: splitParam(sp.get(URL_KEY.jurisdiction)),
  tier: splitParam(sp.get(URL_KEY.tier)),
  origin: splitParam(sp.get(URL_KEY.origin)),
  destination: splitParam(sp.get(URL_KEY.destination)),
  htsChapter: splitParam(sp.get(URL_KEY.htsChapter)),
  needsJurisdiction: sp.get(NEEDS_JUR_PARAM) === '1',
  database: splitParam(sp.get(DATABASE_PARAM)),
});

export const applyFiltersToParams = (
  sp: URLSearchParams,
  f: ProductFilters,
): void => {
  FILTER_FACET_KEYS.forEach((k) => {
    const vals = f[k];
    if (vals && vals.length) sp.set(URL_KEY[k], vals.join(','));
    else sp.delete(URL_KEY[k]);
  });
  if (f.needsJurisdiction) sp.set(NEEDS_JUR_PARAM, '1');
  else sp.delete(NEEDS_JUR_PARAM);
  if (f.database && f.database.length) sp.set(DATABASE_PARAM, f.database.join(','));
  else sp.delete(DATABASE_PARAM);
};

export const countActiveFilters = (f: ProductFilters): number =>
  FILTER_FACET_KEYS.reduce((n, k) => n + (f[k]?.length || 0), 0) +
  (f.needsJurisdiction ? 1 : 0) +
  (f.database?.length || 0);

// Comma-string params for the products-list API call (empty → undefined).
export const filtersToApiParams = (
  f: ProductFilters,
): Record<string, string | undefined> => ({
  status: f.status.join(',') || undefined,
  jurisdiction: f.jurisdiction.join(',') || undefined,
  tier: f.tier.join(',') || undefined,
  origin: f.origin.join(',') || undefined,
  dest: f.destination.join(',') || undefined,
  hts: f.htsChapter.join(',') || undefined,
  needs_jurisdiction: f.needsJurisdiction ? '1' : undefined,
  database: f.database.join(',') || undefined,
});

// ---- Columns ---------------------------------------------------------------

export interface ColumnDef {
  key: string;
  label: string;
  toggleable: boolean;
}

export const PRODUCT_COLUMN_DEFS: ColumnDef[] = [
  { key: 'name', label: 'Product', toggleable: false },
  { key: 'hts_code', label: 'HTS Code', toggleable: true },
  { key: 'country_of_origin', label: 'Origin', toggleable: true },
  { key: 'compliance_status', label: 'Compliance', toggleable: true },
  { key: 'jurisdiction', label: 'Jurisdiction', toggleable: true },
  { key: 'manufacturer', label: 'Manufacturer', toggleable: true },
  { key: 'destinations', label: 'Destinations', toggleable: true },
  { key: 'licenses', label: 'Licenses', toggleable: true },
  { key: 'last_screened_at', label: 'Last Screened', toggleable: true },
  { key: 'last_updated', label: 'Updated', toggleable: true },
];

export const DEFAULT_COLUMNS = [
  'name',
  'hts_code',
  'country_of_origin',
  'compliance_status',
  'jurisdiction',
  'licenses',
  'last_screened_at',
];

const TOGGLEABLE_KEYS = new Set(
  PRODUCT_COLUMN_DEFS.filter((c) => c.toggleable).map((c) => c.key),
);

const isDefaultColumns = (cols: string[]): boolean =>
  cols.length === DEFAULT_COLUMNS.length &&
  DEFAULT_COLUMNS.every((c) => cols.includes(c));

export const parseColumns = (sp: URLSearchParams): string[] => {
  const raw = sp.get('cols');
  if (!raw) return DEFAULT_COLUMNS;
  const chosen = raw
    .split(',')
    .map((s) => s.trim())
    .filter((k) => TOGGLEABLE_KEYS.has(k));
  // Keep def order; 'name' is always first.
  return PRODUCT_COLUMN_DEFS.filter(
    (c) => !c.toggleable || chosen.includes(c.key),
  ).map((c) => c.key);
};

export const applyColumnsToParams = (
  sp: URLSearchParams,
  cols: string[],
): void => {
  if (isDefaultColumns(cols)) {
    sp.delete('cols');
  } else {
    sp.set('cols', cols.filter((k) => TOGGLEABLE_KEYS.has(k)).join(','));
  }
};

// Order an arbitrary set of visible keys by the canonical column order.
export const orderColumns = (visibleKeys: string[]): string[] =>
  PRODUCT_COLUMN_DEFS.filter(
    (c) => !c.toggleable || visibleKeys.includes(c.key),
  ).map((c) => c.key);

export interface SavedViewState {
  filters: ProductFilters;
  columns: string[];
}
